/**
 * JobPostingModal — add a job posting by URL or by pasting the description.
 *
 * Two modes:
 *  - URL mode  : paste a link, backend fetches + extracts (costs 1 credit)
 *  - Paste mode: paste the raw job description text (always free)
 *
 * Both modes accept an optional URL for duplicate detection.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Link, FileText, Loader2 } from 'lucide-react';
import { jobPostingApi } from '@/lib/api';
import type { IngestJobPostingResponse } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

type Mode = 'url' | 'paste';

interface JobPostingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful ingest with the server response */
  onSuccess?: (result: IngestJobPostingResponse) => void;
  /** Open directly in paste mode (e.g. from a failed posting's retry button) */
  initialMode?: Mode;
  /** Pre-fill the URL field */
  initialUrl?: string;
}

export function JobPostingModal({
  open,
  onOpenChange,
  onSuccess,
  initialMode = 'url',
  initialUrl = '',
}: JobPostingModalProps) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [url, setUrl] = useState(initialUrl);
  const [rawText, setRawText] = useState('');
  const [error, setError] = useState('');

  const ingestMutation = useMutation({
    mutationFn: jobPostingApi.ingest,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['job-postings'] });
      onSuccess?.(result);
      resetForm();
      onOpenChange(false);
    },
    onError: (err: Error) => {
      setError(err.message ?? 'Failed to ingest posting. Please try again.');
    },
  });

  const resetForm = () => {
    setUrl(initialUrl);
    setRawText('');
    setError('');
    ingestMutation.reset();
  };

  const handleModeChange = (next: Mode) => {
    setMode(next);
    setError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'url') {
      const trimmed = url.trim();
      if (!trimmed) { setError('Please enter a URL.'); return; }
      try { new URL(trimmed); } catch {
        setError('Please enter a valid URL (include https://).'); return;
      }
      ingestMutation.mutate({ url: trimmed });
    } else {
      const trimmedText = rawText.trim();
      if (trimmedText.length < 100) {
        setError('Please paste at least 100 characters of job description text.');
        return;
      }
      const trimmedUrl = url.trim();
      if (trimmedUrl) {
        try { new URL(trimmedUrl); } catch {
          setError('The URL you entered is not valid (include https://).'); return;
        }
      }
      ingestMutation.mutate({ rawText: trimmedText, url: trimmedUrl || undefined });
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!ingestMutation.isPending) {
      onOpenChange(next);
      if (!next) resetForm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add job posting</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Mode toggle */}
          <div className="flex rounded-lg border p-1 gap-1">
            <button
              type="button"
              onClick={() => handleModeChange('url')}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                mode === 'url'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Link className="h-3.5 w-3.5" />
              From URL
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('paste')}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                mode === 'paste'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <FileText className="h-3.5 w-3.5" />
              Paste text
              <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                Free
              </span>
            </button>
          </div>

          <p className="text-sm text-muted-foreground">
            {mode === 'url'
              ? "Paste a link to any job posting. We'll fetch and extract the details automatically."
              : 'Copy the full job description from the posting and paste it below.'}
          </p>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {mode === 'url' ? (
            <div className="space-y-1.5">
              <Label htmlFor="posting-url">Job posting URL</Label>
              <Input
                id="posting-url"
                type="url"
                placeholder="https://jobs.example.com/senior-engineer-123"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={ingestMutation.isPending}
                autoFocus
              />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="posting-text">Job description</Label>
                <Textarea
                  id="posting-text"
                  placeholder="Paste the full job description here…"
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  disabled={ingestMutation.isPending}
                  rows={10}
                  className="resize-none"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="posting-url-optional">
                  Job posting URL{' '}
                  <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  id="posting-url-optional"
                  type="url"
                  placeholder="https://jobs.example.com/senior-engineer-123"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={ingestMutation.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  Used to detect if you've already added this posting.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={ingestMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={ingestMutation.isPending}>
              {ingestMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Queuing…
                </>
              ) : (
                'Add posting'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
